import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, CheckSquare, BookOpen, FileEdit, Briefcase,
  Sparkles, Clock, ArrowRight, IndianRupee, ClipboardList,
  Trophy, Star, Flame, Hash, TrendingUp, ClipboardCheck,
} from 'lucide-react';
import KudosWidget from '@/components/KudosWidget';

function Stat({ icon: Icon, label, value, accent = 'amber' }) {
  return (
    <Card className="shine-card p-4 rounded-xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
          <div className="mt-1 text-2xl font-extrabold text-white">{value ?? '—'}</div>
        </div>
        <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-amber-500/10 border border-amber-500/30">
          <Icon className="h-5 w-5 text-amber-300" />
        </div>
      </div>
    </Card>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="group">
      <Card className="shine-card p-4 rounded-xl hover:border-amber-400/50 transition cursor-pointer">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-amber-500/10 border border-amber-500/30 flex-shrink-0">
            <Icon className="h-5 w-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-white">{title}</div>
              <ArrowRight className="h-4 w-4 text-amber-300/60 group-hover:translate-x-1 transition" />
            </div>
            <div className="text-xs text-slate-400 mt-1">{desc}</div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function ScoreRing({ score }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const r = 32;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const color = pct >= 75 ? '#39b563' : pct >= 50 ? '#5780ea' : pct >= 30 ? '#fbbf24' : '#f97373';
  return (
    <div className="relative h-24 w-24">
      <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
        <circle cx="40" cy="40" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="7" fill="none" />
        <circle cx="40" cy="40" r={r} stroke={color} strokeWidth="7" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold text-white leading-none">{Math.round(pct)}</span>
        <span className="text-[10px] uppercase text-slate-400 tracking-wider mt-0.5">score</span>
      </div>
    </div>
  );
}

const MEDAL = ['🥇', '🥈', '🥉'];

export default function DashboardPage() {
  const { user, isClient, isManager } = useAuth();
  const [stats, setStats] = useState({});
  const [myTasks, setMyTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [perf, setPerf] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);

  useEffect(() => {
    api.get('/stats/overview').then((r) => setStats(r.data)).catch(() => {});
    if (isClient) {
      api.get('/projects').then((r) => setProjects(r.data)).catch(() => {});
      api.get('/daily-reports/pending-review').then((r) => setPendingReviews(r.data)).catch(() => {});
    } else {
      api.get('/tasks?mine=true').then((r) => setMyTasks(r.data.slice(0, 5))).catch(() => {});
      api.get('/daily-reports/stats/my?period=week').then((r) => setPerf(r.data)).catch(() => {});
      if (isManager) {
        api.get('/daily-reports/stats/leaderboard?period=week').then((r) => setLeaderboard((r.data.leaderboard || []).slice(0, 5))).catch(() => {});
      }
    }
  }, [isClient, isManager]);

  if (isClient) {
    return (
      <div>
        <PageHeader
          icon={LayoutDashboard}
          title={`Welcome, ${user?.name}`}
          subtitle="Your projects and items awaiting your review."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <Stat icon={Briefcase} label="My Projects" value={stats.projects} />
          <Stat icon={FileEdit} label="Change Requests" value={stats.change_requests} />
          <Stat icon={ClipboardCheck} label="Awaiting Your Review" value={pendingReviews.length} />
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="shine-card p-5 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-white">Your Projects</h3>
              <Link to="/" className="text-xs text-amber-300 hover:text-amber-200">All →</Link>
            </div>
            {projects.length === 0 ? (
              <p className="text-slate-400 text-sm">No active projects yet.</p>
            ) : (
              <ul className="divide-y divide-amber-500/5">
                {projects.map((p) => (
                  <li key={p.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-white truncate">{p.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{p.description}</div>
                    </div>
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200 flex-shrink-0">{p.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card className="shine-card p-5 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-white">Awaiting your review</h3>
              <Link to="/reviews" className="text-xs text-amber-300 hover:text-amber-200">View all →</Link>
            </div>
            {pendingReviews.length === 0 ? (
              <p className="text-slate-400 text-sm">Nothing waiting for your review.</p>
            ) : (
              <ul className="divide-y divide-amber-500/5">
                {pendingReviews.slice(0, 5).map((r) => (
                  <li key={r.id} className="py-3">
                    <div className="font-semibold text-white text-sm truncate">{r.item_description}</div>
                    <div className="text-xs text-slate-400 mt-0.5">by {r.user_name} · {r.format} · {new Date(r.date).toLocaleDateString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={LayoutDashboard}
        title={`Welcome, ${user?.name?.split(' ')[0] || ''}`}
        subtitle={`You're logged in as ${user?.role}. Here's what's happening at SHINE.`}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat icon={CheckSquare} label="My Open Tasks" value={stats.my_tasks_open} />
        <Stat icon={CheckSquare} label="My Done Tasks" value={stats.my_tasks_done} />
        <Stat icon={BookOpen} label="SOPs" value={stats.sops} />
        <Stat icon={Briefcase} label="Active Projects" value={stats.active_projects} />
        <Stat icon={FileEdit} label="Open Requests" value={stats.open_change_requests} />
        <Stat icon={Sparkles} label="Open Leads" value={stats.leads_open} />
      </div>

      {/* Peer recognition — daily votes remaining + last month's reveal banner */}
      <KudosWidget />

      {/* My Performance */}
      {perf && (
        <Card className="shine-card p-5 rounded-xl mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <ScoreRing score={perf.score} />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-400">My performance · last 7 days</div>
              <h3 className="text-xl font-extrabold text-white mt-1">
                {perf.score >= 75 ? '🔥 Crushing it!' : perf.score >= 50 ? '👍 Solid output' : perf.score >= 30 ? '📈 Picking up' : '⚡ Start logging work'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <div className="text-xs">
                  <div className="text-slate-400 flex items-center gap-1"><Hash className="h-3 w-3" /> Items</div>
                  <div className="text-lg font-bold text-white">{perf.total_items}</div>
                </div>
                <div className="text-xs">
                  <div className="text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Hours</div>
                  <div className="text-lg font-bold text-white">{perf.total_hours}</div>
                </div>
                <div className="text-xs">
                  <div className="text-slate-400 flex items-center gap-1"><Flame className="h-3 w-3" /> Consistency</div>
                  <div className="text-lg font-bold text-white">{perf.consistency_pct}%</div>
                </div>
                <div className="text-xs">
                  <div className="text-slate-400 flex items-center gap-1"><Star className="h-3 w-3" /> Client rating</div>
                  <div className="text-lg font-bold text-white">{perf.avg_rating ? `${perf.avg_rating}/5` : '—'}</div>
                </div>
              </div>
            </div>
            <Link to="/reports" className="flex-shrink-0">
              <div className="px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/15 transition flex items-center gap-2 font-semibold">
                <ClipboardList className="h-4 w-4" /> Log work <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="shine-card p-5 rounded-xl lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white">My recent tasks</h3>
            <Link to="/tasks" className="text-xs text-amber-300 hover:text-amber-200">View all →</Link>
          </div>
          {myTasks.length === 0 ? (
            <p className="text-slate-400 text-sm">No tasks assigned to you yet.</p>
          ) : (
            <ul className="divide-y divide-amber-500/5">
              {myTasks.map((t) => (
                <li key={t.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-white truncate">{t.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {t.sop_title || 'No SOP linked'} · {t.deadline ? `Due ${new Date(t.deadline).toLocaleDateString()}` : 'No deadline'}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`flex-shrink-0 text-xs ${
                        t.status === 'Done'
                          ? 'border-emerald-500/40 text-emerald-300'
                          : t.status === 'In Progress'
                          ? 'border-sky-500/40 text-sky-300'
                          : t.status === 'Review'
                          ? 'border-purple-500/40 text-purple-300'
                          : 'border-amber-500/40 text-amber-200'
                      }`}
                    >
                      {t.status}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-3">
          <QuickLink to="/reports" icon={ClipboardList} title="Log daily work" desc="Submit each piece of work — count + hours." />
          <QuickLink to="/sops" icon={BookOpen} title="SOP Library" desc="Search and follow procedures." />
          <QuickLink to="/tasks" icon={CheckSquare} title="Tasks" desc="See assignments and track progress." />
          <QuickLink to="/rate-cards" icon={IndianRupee} title="Rate Cards" desc="View current pricing." />
        </div>
      </div>

      {/* Leaderboard for Owner/Operations */}
      {isManager && leaderboard.length > 0 && (
        <Card className="shine-card p-5 rounded-xl mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-300" />
              <h3 className="font-bold text-white">Top performers · this week</h3>
            </div>
            <Link to="/team-performance" className="text-xs text-amber-300 hover:text-amber-200">Full view →</Link>
          </div>
          <ul className="divide-y divide-amber-500/5">
            {leaderboard.map((u, i) => (
              <li key={u.user_id} className="py-3 flex items-center gap-3">
                <div className="w-7 text-center text-lg">{i < 3 ? MEDAL[i] : <span className="text-slate-500 text-sm">{i + 1}</span>}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-white">{u.user_name}</span>
                    <Badge variant="outline" className="border-amber-500/40 text-amber-200 text-[10px]">{u.user_role}</Badge>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-3">
                    <span>{u.items} items</span>
                    <span>{u.hours}h</span>
                    <span>{u.completed} done</span>
                    <span>{u.consistency_pct}% consistency</span>
                    {u.avg_rating && <span>★ {u.avg_rating}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-extrabold shine-gold-text">{u.score.toFixed(0)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">score</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
